import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import I18n from 'I18n';
import compose from 'transmute/compose';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Wizard as WizardComponent, WizardHeaderWithOverview, WizardStep } from 'foundations-components/transitional/components/Wizard';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { View } from 'foundations-components/components/View';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIButton from 'UIComponents/button/UIButton';

// @ts-expect-error untyped
import ResolveReferences from 'reference-resolvers/ResolveReferences';
// @ts-expect-error untyped

import { DEAL_PIPELINE, DEAL_PROPERTY, DEAL_PIPELINE_STAGE
// @ts-expect-error untyped
} from 'reference-resolvers/constants/ReferenceObjectTypes';
// @ts-expect-error untyped
import { isResolved } from 'reference-resolvers/utils';
import CustomizeForecastCategories from './steps/CustomizeForecastCategories';
import CategoryMapping from './steps/ForecastCategoryMapping';
import Finish from './steps/Finish';
import { DEAL_PROPERTIES } from '../../constants/CrmSearchConstants';
import { FINISH_STEP, FORECAST_CATEGORIES_STEP } from '../../constants/WizardStepTypes';
import { setForecastCategories as setForecastCategoriesAction, setDealStageMappings as setDealStageMappingsAction, saveWizard as saveAction } from '../../actions/WizardActions';
import { getForecastCategories, getDealStageMappings } from '../../selectors/wizardSelectors';
import { getWizardStepName, getDuplicateForecastCategoryLabels, convertDealStageReferenceRecordList, convertForecastCategoryReferenceRecordList, getDefaultDealStageMappings, isInputEmpty } from '../../utils/wizardUtils';
import { trackWizardCancel, trackWizardFinish, trackWizardPrevStep, trackWizardNextStep } from '../../utils/usageTracker';
import { useForecastCategoryUsage } from '../../hooks/useForecastCategoryUsage';
import { logRavenException } from 'ui-deal-management-components-lib/utils/ravenWrapperUtil';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Wizard = ({
  dealStages,
  dealStageMappings,
  forecastCategoryProperty,
  forecastCategories,
  onConfirm,
  onReject,
  pipelines,
  save,
  setForecastCategories,
  setDealStageMappings
}) => {
  const [stepIndex, setStepIndex] = useState(FORECAST_CATEGORIES_STEP);
  const [saving, setSaving] = useState(false);
  const [pipelineOptions, setPipelineOptions] = useState([]);
  const {
    data: referencedCategories,
    loading: categoryUsageIsLoading
  } = useForecastCategoryUsage();
  useEffect(() => {
    trackWizardNextStep('forecast-category-setup');
  }, []);
  useEffect(() => {
    if (isResolved(pipelines)) {
      const options = [];
      for (const pipeline of pipelines) {
        options.push({
          text: pipeline.referencedObject.get('label'),
          value: pipeline.referencedObject.get('pipelineId')
        });
      }
      setPipelineOptions(options);
    }
  }, [pipelines]);
  useEffect(() => {
    if (isResolved(forecastCategoryProperty)) {
      const categories = convertForecastCategoryReferenceRecordList(forecastCategoryProperty);
      setForecastCategories(categories);
    }
  }, [forecastCategoryProperty, setForecastCategories]);
  useEffect(() => {
    if (isResolved(dealStages) && isResolved(forecastCategoryProperty)) {
      const convertedDealStages = convertDealStageReferenceRecordList(dealStages);
      const defaultDealStageMappings = getDefaultDealStageMappings(convertedDealStages, forecastCategories);
      setDealStageMappings(defaultDealStageMappings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealStages, forecastCategoryProperty]);
  const isLoaded = isResolved(pipelines) && isResolved(forecastCategoryProperty) && forecastCategories && isResolved(dealStages) && !categoryUsageIsLoading;
  if (!isLoaded) {
    // Migrate to LoadingSpinner when centered issue is fixed
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      grow: true
    });
  }
  const onStepIndexChange = e => {
    const newStepIndex = e.target.value;
    const isMovingBackwards = newStepIndex < stepIndex;
    const wizardStepName = getWizardStepName(newStepIndex);
    switch (newStepIndex) {
      case FINISH_STEP:
        {
          setSaving(true);
          trackWizardFinish(wizardStepName);
          save(dealStageMappings).then(() => {
            setSaving(false);
            setStepIndex(newStepIndex);
          }).catch(error => {
            logRavenException(error, 'Save deal stage mappings in Forecast Category Wizard', {
              dealStageMappings
            });
            FloatingAlertStore.addAlert({
              titleText: I18n.text('wizard.alerts.saveError.titleText'),
              message: I18n.text('wizard.alerts.saveError.message'),
              type: 'danger'
            });
            setSaving(false);
          });
          break;
        }
      default:
        {
          if (isMovingBackwards) {
            trackWizardPrevStep(wizardStepName);
            setStepIndex(newStepIndex);
            return;
          }
          trackWizardNextStep(wizardStepName);
          setStepIndex(newStepIndex);
        }
    }
  };
  const notBackableOrCancellable = stepIndex === FINISH_STEP || saving;
  const anyEmptyLabel = forecastCategories.some(category => isInputEmpty(category.label));
  const duplicateForecastCategoryLabels = getDuplicateForecastCategoryLabels(forecastCategories);
  const disablePrimaryButton = (anyEmptyLabel || !!duplicateForecastCategoryLabels.length) && stepIndex === FORECAST_CATEGORIES_STEP;
  return /*#__PURE__*/_jsxs(WizardComponent, {
    backable: !notBackableOrCancellable,
    cancellable: !notBackableOrCancellable,
    defaultBackButton: /*#__PURE__*/_jsx(UIButton, {
      use: "secondary",
      "data-test-id": "back-button"
    }),
    defaultCancelButton: /*#__PURE__*/_jsx(UIButton, {
      use: "link-on-bright",
      "data-test-id": "cancel-button"
    }),
    defaultDoneButton: /*#__PURE__*/_jsx(UIButton, {
      use: "primary",
      "data-test-id": "next-button"
    }),
    defaultHeading: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "wizard.header"
    }),
    defaultNextButton: saving ? /*#__PURE__*/_jsx(UILoadingButton, {
      loading: true
    }) : /*#__PURE__*/_jsx(UIButton, {
      use: "primary",
      disabled: disablePrimaryButton,
      "data-test-id": "next-button"
    }),
    headerComponent: WizardHeaderWithOverview,
    onConfirm: onConfirm,
    onReject: () => {
      const wizardStepName = getWizardStepName(stepIndex);
      trackWizardCancel(wizardStepName);
      onReject();
    },
    onStepIndexChange: onStepIndexChange,
    stepIndex: stepIndex,
    stickyHeader: false,
    children: [/*#__PURE__*/_jsx(WizardStep, {
      name: I18n.text('wizard.customize.name'),
      "data-test-id": "customize-page",
      children: /*#__PURE__*/_jsx(View, {
        maxInlineSize: "1000px",
        margin: "auto",
        marginBlockStart: "1200",
        children: /*#__PURE__*/_jsx(CustomizeForecastCategories, {
          forecastCategories: forecastCategories,
          referencedCategories: referencedCategories
        })
      })
    }), /*#__PURE__*/_jsx(WizardStep, {
      name: I18n.text('wizard.map.name'),
      nextLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "wizard.buttons.finish"
      }),
      "data-test-id": "mappings-page",
      children: /*#__PURE__*/_jsx(View, {
        maxInlineSize: "1000px",
        margin: "auto",
        marginBlockStart: "1200",
        children: /*#__PURE__*/_jsx(CategoryMapping, {
          forecastCategories: forecastCategories,
          pipelineOptions: pipelineOptions
        })
      })
    }), /*#__PURE__*/_jsx(WizardStep, {
      name: I18n.text('wizard.finish.name'),
      doneLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "wizard.buttons.goToForecast"
      }),
      "data-test-id": "finish-page",
      children: /*#__PURE__*/_jsx(View, {
        maxInlineSize: "1000px",
        margin: "auto",
        marginBlockStart: "1200",
        children: /*#__PURE__*/_jsx(Finish, {})
      })
    })]
  });
};
export default compose(connect(state => ({
  forecastCategories: getForecastCategories(state),
  dealStageMappings: getDealStageMappings(state)
}), {
  setForecastCategories: setForecastCategoriesAction,
  setDealStageMappings: setDealStageMappingsAction,
  save: saveAction
}), ResolveReferences(resolvers => ({
  pipelines: resolvers[DEAL_PIPELINE].all(),
  forecastCategoryProperty: resolvers[DEAL_PROPERTY].byId(DEAL_PROPERTIES.FORECAST_CATEGORY),
  dealStages: resolvers[DEAL_PIPELINE_STAGE].all()
})))(Wizard);